import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import HomePageLayout from "@/components/layout/HomePageLayout";
import PortfolioFeedCard from "@/components/portfolio/PortfolioFeedCard";
import MobilePortfolioModal from "@/components/profile/portfolio/modal/MobilePortfolioModal";
import { useIsMobile } from "@/hooks/use-mobile";
import { useState } from "react";

// Explicit types for service and profile
interface ServiceType {
  label: string;
  slug: string;
  service_roles: { role: string }[];
}

interface ProfileType {
  id: string;
  username: string;
  avatar_url: string;
  profession: string;
  location: string;
  username_slug?: string;
  bio?: string;
  portfolio_items?: any;
  updated_at?: string;
  // Add other required fields as needed
}

const fetchService = async (slug: string): Promise<ServiceType | null> => {
  const { data, error } = await (supabase as any)
    .from("services")
    .select("label, slug, service_roles(role)")
    .eq("slug", slug)
    .single();
  if (error || !data || typeof data !== 'object' || !('label' in data)) return null;
  return data as ServiceType;
};

const fetchProfiles = async (slug: string): Promise<ProfileType[]> => {
  const { data, error } = await (supabase as any)
    .rpc("get_profiles_by_service", { service_slug: slug });
  if (error || !Array.isArray(data)) return [];
  return data as ProfileType[];
};

const ServiceResultsPage = () => {
  const { slug } = useParams();
  const isMobile = useIsMobile();
  const [selectedProfile, setSelectedProfile] = useState<any>(null);
  const [selectedPortfolioItem, setSelectedPortfolioItem] = useState<any>(null);

  const handleOpenPortfolioModal = (profile: any, item: any) => {
    setSelectedProfile(profile);
    setSelectedPortfolioItem(item);
  };

  const handleClosePortfolioModal = () => {
    setSelectedProfile(null);
    setSelectedPortfolioItem(null);
  };

  const { data: service, isLoading: serviceLoading } = useQuery<ServiceType | null>({
    queryKey: ["service", slug],
    queryFn: () => fetchService(slug!),
    enabled: !!slug,
  });

  const { data: profiles, isLoading: profilesLoading } = useQuery<ProfileType[]>({
    queryKey: ["service-profiles", slug],
    queryFn: () => fetchProfiles(slug!),
    enabled: !!slug,
  });

  return (
    <HomePageLayout>
      <div className="container mx-auto px-4">
        {serviceLoading || profilesLoading ? (
          <div className="py-12 text-center text-gray-400">Loading...</div>
        ) : !service ? (
          <div className="py-12 text-center text-gray-400">Service not found.</div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-1 mt-8">
              <h2 className="text-xl font-bold">{service.label}</h2>
            </div>
            <p className="text-gray-500 text-sm mb-6">
              Showing results for {service.label}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
              {(profiles || []).map((profile) => (
                <PortfolioFeedCard 
                  key={profile.id} 
                  profile={profile}
                  onOpenPortfolioModal={handleOpenPortfolioModal}
                  isMobile={isMobile}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Mobile Portfolio Modal */}
      {isMobile && selectedProfile && selectedPortfolioItem && (
        <MobilePortfolioModal
          item={selectedPortfolioItem}
          isOpen={!!selectedPortfolioItem}
          onClose={handleClosePortfolioModal}
          username={selectedProfile.username || selectedProfile.username_slug || ''}
          username_slug={selectedProfile.username_slug || selectedProfile.username || ''}
          profile={selectedProfile}
        />
      )}
    </HomePageLayout>
  );
};

export default ServiceResultsPage; 